import type { Partner } from "@expert/shared-types";

import { getC360ApiClient } from "./c360ApiClient";

export interface C360Interaction {
    interactionId: string;
    interactionLineId: string;
}

const US_PHONE_NUMBER = /^\+1(\d{10})$/;

/** Splits a `+1` number into C360's 10-digit caller number and country code; any other format is left out. */
export function toC360CallerNumber(
    phoneNumber: string | undefined,
): { callerNumber: string; callerCountryCallingCode: string } | Record<string, never> {
    const nationalNumber = phoneNumber && US_PHONE_NUMBER.exec(phoneNumber)?.[1];
    return nationalNumber ? { callerNumber: nationalNumber, callerCountryCallingCode: "1" } : {};
}

const C360_SUMMARY_MAX_LENGTH = 4000;

/** C360 accepts 1–4000 characters, so a longer summary is cut and an empty one left out. */
export function toC360Summary(summary: string | undefined): { summary?: string } {
    const trimmed = summary?.trim();
    return trimmed ? { summary: trimmed.slice(0, C360_SUMMARY_MAX_LENGTH) } : {};
}

/** Starts a C360 interaction for the session, with the number the call came from as C360's `caller_number`. */
export function createInteraction({
    sessionId,
    partner,
    mdn,
}: {
    sessionId: string;
    partner: Partner;
    mdn?: string;
}): Promise<C360Interaction> {
    return getC360ApiClient(sessionId)
        .post("v1/interactions", { json: { partner, sessionId, ...toC360CallerNumber(mdn) } })
        .json<C360Interaction>();
}

/** Adds Expert Workspace's line to an interaction another channel started, such as the IVR. */
export function joinInteraction({
    sessionId,
    partner,
    interactionId,
}: {
    sessionId: string;
    partner: Partner;
    interactionId: string;
}): Promise<C360Interaction> {
    return getC360ApiClient(sessionId)
        .post(`v1/interactions/${encodeURIComponent(interactionId)}/lines`, {
            json: { partner, sessionId },
        })
        .json<C360Interaction>();
}

/** Closes our line on the interaction, with the session summary. The service sets the line's end time. */
export async function wrapInteractionLine({
    sessionId,
    partner,
    interactionLineId,
    summary,
}: {
    sessionId: string;
    partner: Partner;
    interactionLineId: string;
    summary?: string;
}): Promise<void> {
    await getC360ApiClient(sessionId).post(`v1/lines/${encodeURIComponent(interactionLineId)}/wrap`, {
        json: { partner, ...toC360Summary(summary) },
    });
}

export type C360LinkOutcome = "linked" | "no_customer" | "no_account_match" | "multiple_subscriptions";

/** Links the line to the confirmed customer. The service finds the subscription in C360 and replies only the outcome. */
export function linkCustomerToLine({
    sessionId,
    partner,
    interactionLineId,
    mdn,
    billingAccountNumber,
}: {
    sessionId: string;
    partner: Partner;
    interactionLineId: string;
    mdn: string;
    billingAccountNumber: string;
}): Promise<{ outcome: C360LinkOutcome }> {
    return getC360ApiClient(sessionId)
        .post(`v1/lines/${encodeURIComponent(interactionLineId)}/customer`, {
            json: { partner, mdn, billingAccountNumber },
        })
        .json<{ outcome: C360LinkOutcome }>();
}
